<template>
  <section
    class="bg-base-100 p-4 sm:p-6 rounded-2xl shadow-md mx-auto"
    role="region"
    aria-labelledby="bundle-summary-title">
    <!-- Hidden accessible title for the region -->
    <h2 id="bundle-summary-title" class="sr-only">
      {{ $t("pricingSummary.title") }}
    </h2>
    <div
      v-if="!disableFooter"
      class="mb-6 text-sm sm:text-base text-center bg-info/10 p-4 rounded-xl border border-info">
      💡 {{ $t("pricingSummary.clarifier", { months: 12 }) }}
    </div>

    <!-- Pricing Table -->
    <div class="mb-6 overflow-x-auto -mx-2 px-2">
      <PricingTable
        :plans="selectedPlanOptions"
        :popularId="popularId"
        :formattedOneTime="formattedOneTime"
        :formattedMonthlyRow="formattedMonthlyRow"
        :formattedYearlyRow="formattedYearlyRow"
        :formattedRecurringRow="formattedRecurringRow"
        :formattedTaxOnOneTime="formattedTaxOnOneTime"
        :formattedTaxOnRecurring="formattedTaxOnRecurring"
        :formattedTotalDueNow="formattedTotalDueNow"
        :hasOneTime="hasOneTime"
        :hasMonthly="hasMonthly"
        :hasYearly="hasYearly"
        :taxRate="taxRate"
        :billing-interval="billingInterval"
        :formattedTaxOnMonthly="formattedTaxOnMonthly"
        :formattedTaxOnYearly="formattedTaxOnYearly"
        :annualRecurringTotalFormatted="annualRecurringTotalFormatted" />
    </div>

    <!-- Footer with toggle and button -->
    <div v-if="!disableFooter">
      <BillingToggle v-model:current-interval="billingInterval" />

      <CheckoutButton @checkout="emit('checkout')" :href="ctaLink">
        {{ $t("pricingSummary.ctaButton") }}
      </CheckoutButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { usePricingSummary } from "@/features/pricing-summary/composables/usePricingSummary";
import type { PlanOption } from "@/features/planSelection/models/PlanOption";
import PricingTable from "@/features/pricing-summary/components/pricing-table/PricingTable.vue";
import BillingToggle from "@/features/pricing-summary/components/BillingToggle.vue";
import CheckoutButton from "@/features/pricing-summary/components/CheckoutButton.vue";

const props = defineProps<{
  selectedPlanOptions: PlanOption[];
  ctaLink: string;
  billingInterval: "monthly" | "yearly";
  popularId?: string;
  disableFooter?: boolean;
}>();
const emit = defineEmits<{
  (e: "update:billingInterval", value: "monthly" | "yearly"): void;
  (e: "checkout"): void;
}>();

// two‑way binding of billing interval
const billingInterval = ref(props.billingInterval);
watch(billingInterval, val => emit("update:billingInterval", val));

// compute summary
const selectedPlans = computed(() => props.selectedPlanOptions);
const {
  formattedOneTime,
  formattedMonthlyRow,
  formattedYearlyRow,
  formattedRecurringRow,
  formattedTaxOnOneTime,
  formattedTaxOnRecurring,
  formattedTotalDueNow,
  formattedTaxOnMonthly,
  formattedTaxOnYearly,
  annualRecurringTotalFormatted,
  hasOneTime,
  hasMonthly,
  hasYearly,
  taxRate,
} = usePricingSummary(selectedPlans, billingInterval, {
  taxRate: 0,
  locale: "nl-NL",
  currency: "EUR",
});
</script>
